/**
 * Conversation Summary Panel — BUB-128 / BUB-134 / BUB-145
 * Paul ↔ customer WhatsApp summary with tabbed Chat vs Summary layout.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  Grid,
  Segmented,
  Space,
  Spin,
  Typography,
} from "antd";
import { MessageOutlined, ReloadOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import {
  getConversationSummary,
  refreshConversationSummary,
} from "../services/conversationSummaryService.js";
import ConversationDetailTabs from "./ConversationDetailTabs.jsx";
import { normalizeChatMessages } from "./ConversationChatThread.jsx";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

function resolveSummaryMode(summary) {
  if (summary?.summaryMode === "structured") return "structured";
  return "executive";
}

const MODE_OPTIONS = [
  { label: "Executive", value: "executive" },
  { label: "Structured bullets", value: "structured" },
];

function formatGeneratedAt(value, compact) {
  if (!value) return null;
  try {
    return format(new Date(value), compact ? "dd MMM · HH:mm" : "dd MMM yyyy, HH:mm");
  } catch {
    return null;
  }
}

const ConversationSummaryPanel = ({
  leadId,
  initialSummary = null,
  compact = false,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [summary, setSummary] = useState(initialSummary);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [summaryMode, setSummaryMode] = useState(resolveSummaryMode(initialSummary));

  const loadSummary = useCallback(async () => {
    if (!leadId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getConversationSummary(leadId);
      const data = response?.data ?? response;
      setSummary(data);
      setSummaryMode(resolveSummaryMode(data));
    } catch (err) {
      console.error("Error loading conversation summary:", err);
      setError("Failed to load conversation summary");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (initialSummary) {
      setSummary(initialSummary);
      setSummaryMode(resolveSummaryMode(initialSummary));
      return;
    }
    loadSummary();
  }, [initialSummary, loadSummary]);

  const runRefresh = useCallback(
    async (mode) => {
      if (!leadId) return;

      setRefreshing(true);
      setError(null);
      try {
        const response = await refreshConversationSummary(leadId, {
          ...(mode === "structured" ? { summaryMode: "structured" } : {}),
        });
        const data = response?.data ?? response;
        setSummary(data);
        setSummaryMode(resolveSummaryMode(data));
      } catch (err) {
        console.error("Error refreshing conversation summary:", err);
        setError("Failed to refresh conversation summary");
      } finally {
        setRefreshing(false);
      }
    },
    [leadId]
  );

  const handleRefresh = () => runRefresh(summaryMode);

  const handleModeChange = async (nextMode) => {
    setSummaryMode(nextMode);
    await runRefresh(nextMode);
  };

  const messages = useMemo(
    () => normalizeChatMessages(summary?.fullChat),
    [summary?.fullChat]
  );

  if (!leadId) return null;

  const generatedLabel = formatGeneratedAt(
    summary?.generatedAt || summary?.lastRefreshedAt,
    isMobile || compact
  );
  const hasText = Boolean(summary?.text?.trim());
  const hasFullChat =
    Boolean(summary?.fullChat?.transcript?.trim()) ||
    (summary?.fullChat?.messages?.length ?? 0) > 0;
  const quoteResponseText =
    summary?.metadata?.quoteResponseText ||
    summary?.metadata?.quoteResponseIntent ||
    null;

  const chatMaxHeight = compact
    ? isMobile
      ? 360
      : 420
    : isMobile
      ? "calc(100dvh - 280px)"
      : 480;

  const summaryStylePicker = !compact ? (
    <Flex vertical gap={8}>
      <Segmented
        block
        value={summaryMode}
        options={MODE_OPTIONS}
        onChange={handleModeChange}
        disabled={refreshing}
        size={isMobile ? "small" : "middle"}
      />
      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
        Summary style refreshes from n8n. Full chat is always available in the Chat tab.
      </Text>
    </Flex>
  ) : null;

  const cardPadding = compact || isMobile ? 12 : 24;

  return (
    <Flex
      vertical
      style={{
        width: "100%",
        border: "1px solid #f0f0f0",
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <Flex
        align={isMobile ? "stretch" : "center"}
        justify="space-between"
        gap={12}
        wrap={isMobile ? "wrap" : "nowrap"}
        style={{
          padding: `${cardPadding}px ${cardPadding}px ${isMobile ? 10 : cardPadding}px`,
          borderBottom: "1px solid #f5f5f5",
        }}
      >
        <Flex align="center" gap={8} style={{ minWidth: 0, flex: 1 }}>
          <MessageOutlined style={{ color: "#005249", flexShrink: 0 }} />
          <Title
            level={5}
            style={{
              margin: 0,
              fontSize: isMobile ? 15 : 16,
              lineHeight: 1.3,
              whiteSpace: "normal",
            }}
          >
            Paul Conversation
          </Title>
        </Flex>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={refreshing}
          size={isMobile || compact ? "small" : "middle"}
          style={{ alignSelf: isMobile ? "stretch" : "center", flexShrink: 0 }}
          block={isMobile}
        >
          Refresh
        </Button>
      </Flex>

      <Flex vertical gap={isMobile ? 12 : 16} style={{ padding: cardPadding }}>
        {loading && !summary ? (
          <Space>
            <Spin size="small" />
            <Text type="secondary">Loading conversation…</Text>
          </Space>
        ) : (
          <>
            {error && (
              <Alert
                type="error"
                showIcon
                message={error}
                action={
                  <Button size="small" onClick={loadSummary}>
                    Retry
                  </Button>
                }
              />
            )}

            {summary?.n8nError && (
              <Alert
                type="error"
                showIcon
                message={`n8n summary failed (${summary.n8nError.code}${
                  summary.n8nError.httpStatus != null
                    ? ` · HTTP ${summary.n8nError.httpStatus}`
                    : ""
                })`}
                description={
                  summary.n8nError.hint ||
                  "Configure N8N_CONVERSATION_SUMMARY_WEBHOOK_URL and secret, or check n8n workflow logs."
                }
              />
            )}

            {!hasText &&
              !hasFullChat &&
              !summary?.n8nError &&
              summary?.status === "unavailable" && (
                <Alert
                  type="info"
                  showIcon
                  message="No conversation summary yet"
                  description="Tap Refresh to pull the latest Paul ↔ customer thread from n8n."
                />
              )}

            {(hasText || hasFullChat) && (
              <ConversationDetailTabs
                messages={messages}
                summaryText={summary?.text}
                summaryFormat={summary?.format}
                metadata={summary?.metadata}
                messageCount={summary?.messageCount}
                generatedLabel={generatedLabel}
                isMobile={isMobile || compact}
                chatMaxHeight={chatMaxHeight}
                quoteResponseText={quoteResponseText}
                summaryExtra={summaryStylePicker}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default ConversationSummaryPanel;
